import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  X,
  Image as ImageIcon,
  Save,
  AlertCircle,
  CheckCircle,
  Info,
  Settings,
  RotateCcw,
  Move3D,
  ToggleLeft,
  FileImage,
  Sofa,
  Cuboid,
  Layers,
  Box,
  Ruler,
} from "lucide-react";
import { measureGLB } from "../../utils/measureGLB";
import { adminFurniture } from "../../services/adminApi";

/* ── Design tokens (matches project design system) ─────────────────── */
const T = {
  dark: "#0c1118",
  amber: "#111111",
  bg: "#f4f6f8",
  surface: "#ffffff",
  border: "#e8ecf0",
  muted: "#6b7280",
};

const INP = {
  border: `1px solid ${T.border}`,
  borderRadius: 8,
  padding: "10px 14px",
  fontSize: 13,
  width: "100%",
  boxSizing: "border-box",
  fontFamily: "'DM Sans', sans-serif",
  outline: "none",
  background: "#fff",
};
const INP_MONO = { ...INP, fontFamily: "'DM Sans', sans-serif" };

if (typeof document !== "undefined" && !document.getElementById("fd-styles")) {
  const s = document.createElement("style");
  s.id = "fd-styles";
  s.textContent = `
    @keyframes fd-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
    .fd-drop:hover { border-color: #111111 !important; background: rgba(0,0,0,0.02) !important; }
    .fd-toggle:hover { background: #f4f6f8 !important; }
    .fd-step-btn:hover { opacity: 0.8; }
  `;
  document.head.appendChild(s);
}

const categories = [
  "seating",
  "table",
  "storage",
  "bed",
  "appliance",
  "office",
  "gym",
  "decor",
  "other",
];

const anchors3d = [
  { label: "Ground", value: "foot" },
  { label: "Center", value: "center" },
  { label: "User Defined", value: "custom" },
];

function round3(n) {
  if (n === "" || n == null) return "";
  const x = Number(n);
  if (!Number.isFinite(x)) return "";
  return String(Math.round(x * 1000) / 1000);
}

async function measureImagePxFromFile(file) {
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    const dims = await new Promise((resolve, reject) => {
      img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
      img.onerror = reject;
      img.src = url;
    });
    return dims;
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function measureImagePxFromUrl(url) {
  const img = new Image();
  // If same-origin, this is fine. If not, you may need CORS headers.
  return new Promise((resolve, reject) => {
    img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
    img.onerror = reject;
    img.src = url;
  });
}

async function measureGLBFromUrl(url) {
  const res = await fetch(url, { cache: "no-store" });
  if (!res.ok) throw new Error("Failed to download GLB for measurement");
  const blob = await res.blob();
  const file = new File([blob], "model.glb", {
    type: blob.type || "model/gltf-binary",
  });
  return measureGLB(file);
}

export default function FurnitureDrawer({ id, onClose }) {
  const isEdit = !!id;
  const [busy, setBusy] = useState(false);
  const [mErr, setMErr] = useState("");
  const [currentStep, setCurrentStep] = useState(1);

  // measured (read-only display)
  const [measuredPlanPx, setMeasuredPlanPx] = useState(null); // {w,h}
  const [measuredGlbM, setMeasuredGlbM] = useState(null); // {widthM,depthM,heightM,minY...}

  // object URLs to revoke
  const objectUrlsRef = useRef({ plan: null, thumb: null, mesh: null });

  const [form, setForm] = useState({
    code: "",
    name: "",
    category: "other",
    active: true,
    tags: "",
    notes: "",

    // 2D constant (PX) - editable
    planDisplayWidthPx: "",
    planDisplayHeightPx: "",

    // 3D constants (meters) - editable
    targetWidthM: "",
    targetDepthM: "",
    targetHeightM: "",

    // 3D anchor (Ground/Center/UserDefined)
    pivot: "foot",
    pivotOffsetY: "",

    // behavior
    allowRotate: true,
    allowScale: true,
    minScale: "",
    maxScale: "",

    // files
    mesh: null,
    thumbnail: null,
    planView: null,
  });

  const [previewUrls, setPreviewUrls] = useState({
    mesh: null, // can be url or "existing"
    thumbnail: null, // url
    planView: null, // url
  });

  function parseTags(s) {
    return String(s || "")
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean);
  }

  function revokeObjectUrl(key) {
    const prev = objectUrlsRef.current[key];
    if (prev && prev.startsWith("blob:")) {
      try {
        URL.revokeObjectURL(prev);
      } catch {}
    }
    objectUrlsRef.current[key] = null;
  }

  // ---------- load existing ----------
  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!isEdit) return;

      try {
        const f = await adminFurniture.get(id);
        if (cancelled) return;

        setForm((prev) => ({
          ...prev,
          code: f.code || "",
          name: f.name || "",
          category: f.category || "other",
          active: f.active ?? true,
          tags: (f.tags || []).join(", "),
          notes: f.notes || "",

          // 2D constants
          planDisplayWidthPx: f.plan?.displayPx?.w ?? "",
          planDisplayHeightPx: f.plan?.displayPx?.h ?? "",

          // 3D constants
          targetWidthM: f.size3d?.w ?? "",
          targetDepthM: f.size3d?.d ?? "",
          targetHeightM: f.size3d?.h ?? "",

          // anchor
          pivot: f.anchor3d?.mode ?? "foot",
          pivotOffsetY: String(f.anchor3d?.offsetY ?? ""),

          // behavior
          allowRotate: f.allowRotate ?? true,
          allowScale: f.allowScale ?? true,
          minScale: f.minScale ?? "",
          maxScale: f.maxScale ?? "",

          // files (not set on edit)
          mesh: null,
          thumbnail: null,
          planView: null,
        }));

        setPreviewUrls({
          mesh: f.model?.meshUrl ? "existing" : null,
          thumbnail: f.model?.thumbUrl || null,
          planView: f.plan?.url || null,
        });

        // Measure plan px from existing URL (nice UX, read-only display)
        if (f.plan?.url) {
          try {
            const px = await measureImagePxFromUrl(f.plan.url);
            if (!cancelled) setMeasuredPlanPx(px);
          } catch {
            // ignore
          }
        }

        // Measure GLB from existing URL (download + parse)
        if (f.model?.meshUrl) {
          try {
            const m = await measureGLBFromUrl(f.model.meshUrl);
            if (!cancelled) setMeasuredGlbM(m);
          } catch {
            // ignore (GLB can be large / CORS / etc.)
          }
        }
      } catch (e) {
        console.error("Failed to load furniture:", e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id, isEdit]);

  // cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      revokeObjectUrl("plan");
      revokeObjectUrl("thumb");
      revokeObjectUrl("mesh");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const steps = useMemo(
    () => [
      { number: 1, title: "Basics", description: "Code, name, category, tags", icon: Info },
      { number: 2, title: "Files", description: "TOPDOWN PNG, Thumbnail, GLB", icon: Layers },
      { number: 3, title: "Constants", description: "2D PX size + 3D target size", icon: Ruler },
      { number: 4, title: "Layout & Behavior", description: "3D anchor + rotate/scale limits", icon: Settings },
    ],
    []
  );

  // ---------- validation ----------
  const canStep2 = !!form.name && !!form.code && !!form.category;

  const hasPlan = !!form.planView || !!previewUrls.planView;
  const hasMesh = !!form.mesh || !!previewUrls.mesh;

  const canStep3 = canStep2 && hasPlan && hasMesh;

  const planPxOk =
    String(form.planDisplayWidthPx).trim() !== "" &&
    String(form.planDisplayHeightPx).trim() !== "" &&
    Number(form.planDisplayWidthPx) > 0 &&
    Number(form.planDisplayHeightPx) > 0;

  const targetOk =
    String(form.targetWidthM).trim() !== "" &&
    String(form.targetDepthM).trim() !== "" &&
    Number(form.targetWidthM) > 0 &&
    Number(form.targetDepthM) > 0;

  const canStep4 = canStep3 && planPxOk && targetOk;

  const customOk =
    form.pivot !== "custom" ||
    (String(form.pivotOffsetY).trim() !== "" && Number.isFinite(Number(form.pivotOffsetY)));

  const canSave = canStep4 && customOk;

  // ---------- file handlers ----------
  async function onPlanPicked(file) {
    if (!file) return;
    setMErr("");

    revokeObjectUrl("plan");
    const url = URL.createObjectURL(file);
    objectUrlsRef.current.plan = url;

    setForm((f) => ({ ...f, planView: file }));
    setPreviewUrls((p) => ({ ...p, planView: url }));

    try {
      const px = await measureImagePxFromFile(file);
      setMeasuredPlanPx(px);

      // set defaults for display px if empty
      setForm((f) => ({
        ...f,
        planDisplayWidthPx:
          String(f.planDisplayWidthPx).trim() === "" ? String(px.w) : f.planDisplayWidthPx,
        planDisplayHeightPx:
          String(f.planDisplayHeightPx).trim() === "" ? String(px.h) : f.planDisplayHeightPx,
      }));
    } catch {
      // ignore
    }
  }

  async function onThumbnailPicked(file) {
    if (!file) return;

    revokeObjectUrl("thumb");
    const url = URL.createObjectURL(file);
    objectUrlsRef.current.thumb = url;

    setForm((f) => ({ ...f, thumbnail: file }));
    setPreviewUrls((p) => ({ ...p, thumbnail: url }));
  }

  async function onMeshPicked(file) {
    if (!file) return;
    try {
      setMErr("");
      setBusy(true);

      const m = await measureGLB(file);
      setMeasuredGlbM(m);

      revokeObjectUrl("mesh");
      const url = URL.createObjectURL(file);
      objectUrlsRef.current.mesh = url;

      setForm((f) => {
        // default target dims if empty
        const nextTW = String(f.targetWidthM).trim() === "" ? round3(m.widthM) : f.targetWidthM;
        const nextTD = String(f.targetDepthM).trim() === "" ? round3(m.depthM) : f.targetDepthM;
        const nextTH = String(f.targetHeightM).trim() === "" ? round3(m.heightM) : f.targetHeightM;

        return {
          ...f,
          mesh: file,
          targetWidthM: nextTW,
          targetDepthM: nextTD,
          targetHeightM: nextTH,
        };
      });

      setPreviewUrls((p) => ({ ...p, mesh: url }));
    } catch (e) {
      console.error(e);
      setMErr("Could not read GLB. Please ensure it's a valid GLB/GLTF file.");
    } finally {
      setBusy(false);
    }
  }

  // ---------- submit ----------
  async function submit() {
    setBusy(true);
    try {
      const fd = new FormData();

      // identity
      fd.append("code", form.code);
      fd.append("name", form.name);
      fd.append("category", form.category);
      fd.append("active", String(form.active));
      parseTags(form.tags).forEach((t) => fd.append("tags", t));
      if (form.notes) fd.append("notes", form.notes);

      // 2D constants (PX)
      fd.append("planDisplayWidthPx", String(form.planDisplayWidthPx));
      fd.append("planDisplayHeightPx", String(form.planDisplayHeightPx));

      // 3D constants (m)
      fd.append("targetWidthM", String(form.targetWidthM));
      fd.append("targetDepthM", String(form.targetDepthM));
      if (String(form.targetHeightM).trim() !== "") fd.append("targetHeightM", String(form.targetHeightM));

      // 3D anchor
      fd.append("pivot", String(form.pivot || "foot"));
      if (form.pivot === "custom") fd.append("pivotOffsetY", String(form.pivotOffsetY));

      // behavior
      fd.append("allowRotate", String(form.allowRotate));
      fd.append("allowScale", String(form.allowScale));
      if (String(form.minScale).trim() !== "") fd.append("minScale", String(form.minScale));
      if (String(form.maxScale).trim() !== "") fd.append("maxScale", String(form.maxScale));

      // files
      if (form.mesh) fd.append("mesh", form.mesh);
      if (form.thumbnail) fd.append("thumbnail", form.thumbnail);
      if (form.planView) fd.append("planView", form.planView);

      if (isEdit) await adminFurniture.update(id, fd);
      else await adminFurniture.create(fd);

      onClose(true);
    } catch (e) {
      console.error(e);
      alert(e.message || "Save failed");
    } finally {
      setBusy(false);
    }
  }

  return createPortal(
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 9999 }}>
      <div style={{ position: "absolute", right: 0, top: 0, height: "100%", width: "100%", maxWidth: 720, background: T.surface, display: "flex", flexDirection: "column", boxShadow: "-4px 0 40px rgba(0,0,0,0.15)", fontFamily: "'DM Sans', sans-serif" }}>
        {/* Header */}
        <div
          style={{ padding: "20px 24px", borderBottom: `1px solid ${T.border}`, background: T.dark, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}
        >
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: "#fff", margin: 0 }}>{isEdit ? "Edit Furniture" : "New Furniture"}</h2>
            <p style={{ fontSize: 13, color: "rgba(255,255,255,0.55)", marginTop: 4 }}>
              {isEdit ? "Update furniture item" : "Add item to the library"}
            </p>
          </div>
          <button onClick={() => onClose(false)} style={{ background: "rgba(255,255,255,0.1)", border: "none", borderRadius: 8, cursor: "pointer", padding: 8, color: "#fff", display: "flex" }}>
            <X size={18} />
          </button>
        </div>

        {/* Stepper */}
        <div style={{ padding: "14px 24px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 8, flexShrink: 0, flexWrap: "wrap" }}>
          {steps.map((step, idx) => (
            <div key={step.number} style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <button className="fd-step-btn" onClick={() => setCurrentStep(step.number)} style={{ width: 28, height: 28, borderRadius: "50%", border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700, background: currentStep === step.number ? T.amber : currentStep > step.number ? "#16a34a" : T.border, color: currentStep === step.number ? T.dark : currentStep > step.number ? "#fff" : T.muted, display: "flex", alignItems: "center", justifyContent: "center" }}>
                {currentStep > step.number ? <CheckCircle size={14} /> : step.number}
              </button>
              <span style={{ fontSize: 12, fontWeight: currentStep === step.number ? 700 : 400, color: currentStep >= step.number ? T.dark : T.muted }}>{step.title}</span>
              {idx < steps.length - 1 && <div style={{ width: 20, height: 2, background: currentStep > step.number ? "#16a34a" : T.border, borderRadius: 1 }} />}
            </div>
          ))}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: 24 }}>
          {/* Step 1: Basics */}
          {currentStep === 1 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              <StepHeader Icon={Info} title="Basic Info" description="Identity and how it appears in the library." />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <FormField label="Furniture Code" required>
                  <input style={INP_MONO} placeholder="e.g., CHAIR_OFFICE_01" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
                </FormField>
                <FormField label="Display Name" required>
                  <input style={INP} placeholder="e.g., Office Chair" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </FormField>
                <FormField label="Category" required>
                  <select style={INP} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                    {categories.map((c) => <option key={c} value={c} style={{ textTransform: "capitalize" }}>{c}</option>)}
                  </select>
                </FormField>
                <FormField label="Status">
                  <label style={{ ...INP, display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, color: T.muted, fontSize: 13 }}>
                      <ToggleLeft size={16} />
                      <span>Active</span>
                    </div>
                    <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} style={{ width: 15, height: 15 }} />
                  </label>
                </FormField>
              </div>
              <FormField label="Tags (comma separated)">
                <input style={INP} placeholder="e.g., modern, black, ergonomic" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} />
              </FormField>
              <FormField label="Notes">
                <textarea style={{ ...INP, resize: "vertical" }} rows={3} placeholder="Optional notes..." value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </FormField>
            </div>
          )}

          {/* Step 2: Files */}
          {currentStep === 2 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              <StepHeader Icon={Layers} title="Upload Files" description="TopDown PNG for 2D canvas, thumbnail, and GLB for 3D." />

              <div>
                <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
                  TopDown Plan PNG <span style={{ color: "#dc2626" }}>*</span>
                </label>
                <FileUploadZone accept="image/*" onFileSelect={onPlanPicked} previewUrl={previewUrls.planView} Icon={FileImage} title="Drop TopDown PNG here" subtitle="Transparent PNG recommended" isImage />
                <p style={{ fontSize: 12, color: T.muted, marginTop: 6 }}>
                  Measured: <b>{measuredPlanPx ? `${measuredPlanPx.w} × ${measuredPlanPx.h} px` : "Upload to measure"}</b>
                </p>
              </div>

              <div>
                <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Thumbnail (optional)</label>
                <FileUploadZone accept="image/*" onFileSelect={onThumbnailPicked} previewUrl={previewUrls.thumbnail} Icon={ImageIcon} title="Drop thumbnail here" subtitle="PNG/JPG — used in furniture panel" isImage />
              </div>

              <div>
                <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
                  GLB Model <span style={{ color: "#dc2626" }}>*</span>
                </label>
                <FileUploadZone accept=".glb,.gltf" onFileSelect={onMeshPicked} previewUrl={previewUrls.mesh} Icon={Cuboid} title="Drop GLB here" subtitle="Auto-measures W × D × H on upload" busy={busy} />
                {mErr && (
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: 12, background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 8, marginTop: 10 }}>
                    <AlertCircle size={15} color="#dc2626" style={{ flexShrink: 0, marginTop: 1 }} />
                    <span style={{ fontSize: 13, color: "#7f1d1d" }}>{mErr}</span>
                  </div>
                )}
                <p style={{ fontSize: 12, color: T.muted, marginTop: 6 }}>
                  Measured: <b>{measuredGlbM ? `${round3(measuredGlbM.widthM)} × ${round3(measuredGlbM.depthM)} × ${round3(measuredGlbM.heightM)} m` : "Upload to measure"}</b>
                </p>
              </div>
            </div>
          )}

          {/* Step 3: Constants */}
          {currentStep === 3 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              <StepHeader Icon={Ruler} title="Constants" description="Set display sizes for 2D (px) and 3D target size (meters)." />

              <div style={{ padding: 16, background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 10 }}>
                <div style={{ fontWeight: 700, color: "#1e3a8a", marginBottom: 4 }}>2D Plan Display Size (px)</div>
                <div style={{ fontSize: 12, color: "#3b82f6", marginBottom: 12 }}>
                  PNG measured: <b>{measuredPlanPx ? `${measuredPlanPx.w} × ${measuredPlanPx.h} px` : "Unknown — upload PNG first"}</b>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <FormField label="Display Width (px)" required>
                    <input style={{ ...INP_MONO, border: "1px solid #bfdbfe" }} placeholder="e.g. 128" value={form.planDisplayWidthPx} onChange={(e) => setForm({ ...form, planDisplayWidthPx: e.target.value })} />
                  </FormField>
                  <FormField label="Display Height (px)" required>
                    <input style={{ ...INP_MONO, border: "1px solid #bfdbfe" }} placeholder="e.g. 128" value={form.planDisplayHeightPx} onChange={(e) => setForm({ ...form, planDisplayHeightPx: e.target.value })} />
                  </FormField>
                </div>
              </div>

              <div style={{ padding: 16, background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 10 }}>
                <div style={{ fontWeight: 700, color: "#14532d", marginBottom: 4 }}>3D Target Size (meters)</div>
                <div style={{ fontSize: 12, color: "#16a34a", marginBottom: 12 }}>
                  GLB measured: <b>{measuredGlbM ? `${round3(measuredGlbM.widthM)} × ${round3(measuredGlbM.depthM)} × ${round3(measuredGlbM.heightM)} m` : "Unknown — upload GLB first"}</b>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12 }}>
                  <FormField label="Width (m)" required>
                    <input style={{ ...INP_MONO, border: "1px solid #bbf7d0" }} placeholder="0.60" value={form.targetWidthM} onChange={(e) => setForm({ ...form, targetWidthM: e.target.value })} />
                  </FormField>
                  <FormField label="Depth (m)" required>
                    <input style={{ ...INP_MONO, border: "1px solid #bbf7d0" }} placeholder="0.55" value={form.targetDepthM} onChange={(e) => setForm({ ...form, targetDepthM: e.target.value })} />
                  </FormField>
                  <FormField label="Height (m)">
                    <input style={{ ...INP_MONO, border: "1px solid #bbf7d0" }} placeholder="optional" value={form.targetHeightM} onChange={(e) => setForm({ ...form, targetHeightM: e.target.value })} />
                  </FormField>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Layout & Behavior */}
          {currentStep === 4 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              <StepHeader Icon={Settings} title="Layout & Behavior" description="3D anchor placement and user interaction controls." />

              <div>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>3D Anchor</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  <FormField label="Anchor Mode">
                    <select style={INP} value={form.pivot} onChange={(e) => setForm({ ...form, pivot: e.target.value })}>
                      {anchors3d.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
                    </select>
                  </FormField>
                  {form.pivot === "custom" && (
                    <FormField label="Offset Y (m)" required>
                      <input style={INP_MONO} placeholder="e.g. 0.12" value={form.pivotOffsetY} onChange={(e) => setForm({ ...form, pivotOffsetY: e.target.value })} />
                    </FormField>
                  )}
                </div>
              </div>

              <div>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>User Controls</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <ToggleField label="Allow Rotation" description="Users can rotate this furniture in the scene" Icon={RotateCcw} checked={form.allowRotate} onChange={(v) => setForm({ ...form, allowRotate: v })} />
                  <ToggleField label="Allow Scale" description="Users can resize this furniture" Icon={Move3D} checked={form.allowScale} onChange={(v) => setForm({ ...form, allowScale: v })} />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 16 }}>
                  <FormField label="Min Scale">
                    <input style={INP_MONO} placeholder="0.5" value={form.minScale} onChange={(e) => setForm({ ...form, minScale: e.target.value })} />
                  </FormField>
                  <FormField label="Max Scale">
                    <input style={INP_MONO} placeholder="2.0" value={form.maxScale} onChange={(e) => setForm({ ...form, maxScale: e.target.value })} />
                  </FormField>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: "16px 24px", borderTop: `1px solid ${T.border}`, background: T.bg, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div>
            {currentStep > 1 && (
              <button onClick={() => setCurrentStep(currentStep - 1)} style={{ padding: "8px 16px", border: `1px solid ${T.border}`, background: T.surface, borderRadius: 8, cursor: "pointer", fontSize: 13, fontFamily: "'DM Sans', sans-serif" }}>Previous</button>
            )}
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={() => onClose(false)} style={{ padding: "8px 16px", border: `1px solid ${T.border}`, background: T.surface, borderRadius: 8, cursor: "pointer", fontSize: 13, fontFamily: "'DM Sans', sans-serif" }}>Cancel</button>
            {currentStep < 4 ? (
              <button
                onClick={() => setCurrentStep(currentStep + 1)}
                disabled={
                  (currentStep === 1 && !canStep2) ||
                  (currentStep === 2 && !canStep3) ||
                  (currentStep === 3 && !canStep4) || busy
                }
                style={{ padding: "8px 18px", background: ((currentStep === 1 && canStep2) || (currentStep === 2 && canStep3) || (currentStep === 3 && canStep4)) ? T.amber : T.border, color: ((currentStep === 1 && canStep2) || (currentStep === 2 && canStep3) || (currentStep === 3 && canStep4)) ? T.dark : T.muted, border: "none", borderRadius: 8, cursor: ((currentStep === 1 && canStep2) || (currentStep === 2 && canStep3) || (currentStep === 3 && canStep4)) ? "pointer" : "not-allowed", fontSize: 13, fontWeight: 700, fontFamily: "'DM Sans', sans-serif" }}
              >
                Continue
              </button>
            ) : (
              <button
                onClick={submit}
                disabled={!canSave || busy}
                style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 20px", background: canSave ? "#16a34a" : T.border, color: canSave ? "#fff" : T.muted, border: "none", borderRadius: 8, cursor: canSave && !busy ? "pointer" : "not-allowed", fontSize: 13, fontWeight: 700, fontFamily: "'DM Sans', sans-serif", opacity: busy ? 0.7 : 1 }}
              >
                {busy ? <div style={{ width: 14, height: 14, border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff", borderRadius: "50%", animation: "fd-spin 0.8s linear infinite" }} /> : <Save size={15} />}
                {busy ? "Saving..." : "Save Furniture"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ---------------- UI bits (matches AssetDrawer design system) ---------------- */

function StepHeader({ Icon, title, description }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 14, paddingBottom: 16, borderBottom: `1px solid ${T.border}` }}>
      <div style={{ width: 44, height: 44, background: `${T.amber}22`, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon size={22} color={T.amber} />
      </div>
      <div>
        <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0, fontFamily: "'DM Sans', sans-serif" }}>{title}</h3>
        <p style={{ fontSize: 13, color: T.muted, marginTop: 4, fontFamily: "'DM Sans', sans-serif" }}>{description}</p>
      </div>
    </div>
  );
}

function FormField({ label, required, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: T.muted, fontFamily: "'DM Sans', sans-serif" }}>
        {label}{required && <span style={{ color: "#dc2626", marginLeft: 3 }}>*</span>}
      </label>
      {children}
    </div>
  );
}

function ToggleField({ label, description, Icon, checked, onChange }) {
  return (
    <div className="fd-toggle" style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", border: `1px solid ${T.border}`, borderRadius: 8, cursor: "pointer" }}>
      <div style={{ width: 38, height: 38, background: T.bg, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon size={18} color={T.muted} />
      </div>
      <div style={{ flex: 1 }}>
        <label style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 13, fontFamily: "'DM Sans', sans-serif" }}>{label}</div>
            <div style={{ fontSize: 12, color: T.muted, fontFamily: "'DM Sans', sans-serif" }}>{description}</div>
          </div>
          <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} style={{ width: 15, height: 15 }} />
        </label>
      </div>
    </div>
  );
}

function FileUploadZone({ accept, onFileSelect, previewUrl, Icon, title, subtitle, busy, isImage }) {
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragActive(e.type === "dragenter" || e.type === "dragover");
  };

  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) onFileSelect(e.dataTransfer.files[0]);
  };

  return (
    <div className="fd-drop" style={{ position: "relative", border: `2px dashed ${dragActive ? T.amber : T.border}`, borderRadius: 10, padding: 24, textAlign: "center", background: dragActive ? "#fffbeb" : T.bg, transition: "all 0.15s" }} onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}>
      <input type="file" accept={accept} onChange={(e) => e.target.files?.[0] && onFileSelect(e.target.files[0])} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0, cursor: "pointer" }} disabled={busy} />
      {previewUrl && isImage ? (
        <div>
          <img src={previewUrl} alt="Preview" style={{ width: 80, height: 80, objectFit: "contain", borderRadius: 8, margin: "0 auto 8px", background: "#fff", border: `1px solid ${T.border}`, padding: 4 }} />
          <p style={{ fontSize: 13, color: "#16a34a", fontWeight: 600 }}>Image selected</p>
        </div>
      ) : previewUrl ? (
        <div>
          <Cuboid size={40} color="#16a34a" style={{ margin: "0 auto 8px", display: "block" }} />
          <p style={{ fontSize: 13, color: "#16a34a", fontWeight: 600 }}>3D model selected</p>
        </div>
      ) : (
        <div>
          <Icon size={40} color={dragActive ? T.amber : T.border} style={{ margin: "0 auto 12px", display: "block" }} />
          <p style={{ fontSize: 14, fontWeight: 600, color: T.dark, marginBottom: 4, fontFamily: "'DM Sans', sans-serif" }}>{dragActive ? "Drop file here" : title}</p>
          <p style={{ fontSize: 12, color: T.muted, fontFamily: "'DM Sans', sans-serif" }}>{subtitle}</p>
          {busy && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginTop: 10 }}>
              <div style={{ width: 14, height: 14, border: `2px solid ${T.amber}`, borderTopColor: "transparent", borderRadius: "50%", animation: "fd-spin 0.8s linear infinite" }} />
              <span style={{ fontSize: 13, color: T.amber, fontFamily: "'DM Sans', sans-serif" }}>Processing...</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}